import React from 'react';
import { Hexagon, ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

export default function PrivacyPage() {
  const navigate = useNavigate();

  return (
    <div className="min-h-screen bg-[#09090B]">
      <header className="fixed top-0 left-0 right-0 z-50 border-b border-white/5 bg-[#09090B]/80 backdrop-blur-xl">
        <div className="max-w-4xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate('/')} className="flex items-center gap-3 group" data-testid="privacy-logo-btn">
            <Hexagon className="w-7 h-7 text-amber-500 fill-amber-500/20" />
            <span className="font-heading font-black text-lg">
              <span className="text-white">Honey</span>
              <span className="text-amber-500">Pot</span>
            </span>
          </button>
          <button
            onClick={() => navigate(-1)}
            className="text-zinc-400 hover:text-white text-sm flex items-center gap-1.5 transition-colors"
            data-testid="privacy-back-btn"
          >
            <ArrowLeft className="w-4 h-4" />
            Back
          </button>
        </div>
      </header>

      <main className="max-w-3xl mx-auto px-6 pt-28 pb-20" data-testid="privacy-content">
        <h1 className="font-heading font-black text-3xl text-white mb-2">Privacy Policy</h1>
        <p className="text-sm text-zinc-500 mb-10">Last updated: February 2026</p>

        <div className="space-y-8 text-sm text-zinc-300 leading-relaxed">
          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">1. What Honey Pot Does</h2>
            <p>
              Honey Pot is a browser extension that detects and preserves creator affiliate links on supported e-commerce and affiliate network websites. When you visit a page containing affiliate links, the extension identifies them and prevents other extensions from overwriting the original creator's referral tag.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">2. Data We Collect</h2>
            <p className="mb-3">We collect the minimum data necessary to provide the service:</p>
            <ul className="list-disc pl-5 space-y-2 text-zinc-400">
              <li><span className="text-zinc-300 font-medium">Account information:</span> Name, email address, and encrypted password when you create an account.</li>
              <li><span className="text-zinc-300 font-medium">Protected link metadata:</span> The domain, affiliate network, and creator tag of links the extension protects. We do <strong className="text-white">not</strong> store the full URL or any page content.</li>
              <li><span className="text-zinc-300 font-medium">Points &amp; activity:</span> Your earned points, level, and streak data for the rewards system.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">3. Data We Do NOT Collect</h2>
            <ul className="list-disc pl-5 space-y-2 text-zinc-400">
              <li>Browsing history or full page URLs</li>
              <li>Personal financial information or purchase details</li>
              <li>Cookies, form data, or passwords from other sites</li>
              <li>Data from pages that do not contain affiliate links</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">4. How We Use Data</h2>
            <ul className="list-disc pl-5 space-y-2 text-zinc-400">
              <li>To detect and preserve affiliate links on supported sites</li>
              <li>To track your points, level progression, and streaks</li>
              <li>To display community leaderboards and aggregated impact statistics</li>
              <li>To send account verification emails (via our email provider)</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">5. Extension Permissions</h2>
            <p className="mb-3">Honey Pot requests the following browser permissions:</p>
            <ul className="list-disc pl-5 space-y-2 text-zinc-400">
              <li><span className="text-zinc-300 font-medium">storage:</span> To save your login session and preferences locally in the browser.</li>
              <li><span className="text-zinc-300 font-medium">activeTab:</span> To check the current page for affiliate links when you click the extension icon.</li>
              <li><span className="text-zinc-300 font-medium">webNavigation:</span> To detect when you navigate to a supported affiliate site.</li>
              <li><span className="text-zinc-300 font-medium">Host permissions (affiliate domains only):</span> The content script only runs on known affiliate network domains (Amazon, ShareASale, CJ, Rakuten, Impact, Awin, eBay, ClickBank, FlexOffers, Skimlinks) to scan for and protect creator links.</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">6. Data Sharing</h2>
            <p>
              We do not sell, trade, or share your personal data with any third parties. Aggregated, anonymized statistics (e.g., total links protected across all users) may be displayed publicly on leaderboards and community impact sections.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">7. Data Storage &amp; Security</h2>
            <p>
              Your data is stored securely in an encrypted database. Passwords are hashed using bcrypt. Authentication tokens are signed with JWT. We use HTTPS for all data transmission between the extension and our servers.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">8. Your Rights</h2>
            <p>You can:</p>
            <ul className="list-disc pl-5 space-y-2 text-zinc-400 mt-2">
              <li>Delete your account at any time, which removes all associated data</li>
              <li>Disable the extension or revoke its permissions through Chrome settings</li>
              <li>Request a copy of your data by contacting us</li>
            </ul>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">9. Changes to This Policy</h2>
            <p>
              We may update this policy from time to time. Significant changes will be communicated through the extension's announcement system or via email. Continued use of Honey Pot after changes constitutes acceptance of the updated policy.
            </p>
          </section>

          <section>
            <h2 className="font-heading font-bold text-lg text-white mb-3">10. Contact</h2>
            <p>
              If you have questions about this privacy policy or your data, please reach out via the web app's settings page or email us at the address listed in the Chrome Web Store listing.
            </p>
          </section>
        </div>
      </main>

      <footer className="border-t border-white/5 py-8 px-6">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Hexagon className="w-5 h-5 text-amber-500 fill-amber-500/20" />
            <span className="font-heading font-bold text-sm text-zinc-500">HoneyPot</span>
          </div>
          <p className="text-xs text-zinc-600">Protecting the creator economy, one link at a time.</p>
        </div>
      </footer>
    </div>
  );
}
